


import DB from "./private/db";
import model from "../../model";
import { IDBOptions, IDBTOptions, DBEntityInstantiator, IPatchData, IWorkspaceTable, IWorkspaceDB } from '../../interface/db';
import sitePatchMap from './private/sitePatchMap';
import { DexieDBMap } from "./private/db.if";
import IMapProperty from "../../model/private/IMapProperty";

import IBaseDto from "../../model/private/IBaseDto";
import IBackgroundModeDto from "../../model/BackgroundModeDto.if";
import IBeaconDto from "../../model/BeaconDto.if";
import IBeaconSensitivityDto from "../../model/BeaconSensitivityDto.if";
import ICountryDto from "../../model/CountryDto.if";
import IFloorPlanDto from "../../model/FloorPlanDto.if";
import IFontAlignmentDto from "../../model/FontAlignmentDto.if";
import IFontFamilyDto from "../../model/FontFamilyDto.if";
import IFontSizeUnitDto from "../../model/FontSizeUnitDto.if";
import IFontStyleDto from "../../model/FontStyleDto.if";
import ILanguageDto from "../../model/LanguageDto.if";
import IMediumDto from "../../model/MediumDto.if";
import IMenuItemDto from "../../model/MenuItemDto.if";
import IMenuItemTranslationDto from "../../model/MenuItemTranslationDto.if";
import IPinTypeDto from "../../model/PinTypeDto.if";
import IScreenOrientationDto from "../../model/ScreenOrientationDto.if";
import ISiteDto from "../../model/SiteDto.if";
import ISiteObjectDto from "../../model/SiteObjectDto.if";
import ISiteObjectKeywordDto from "../../model/SiteObjectKeywordDto.if";
import ISiteObjectOnFloorPlanDto from "../../model/SiteObjectOnFloorPlanDto.if";
import ISiteObjectTranslationDto from "../../model/SiteObjectTranslationDto.if";
import ISiteObjectTypeDto from "../../model/SiteObjectTypeDto.if";
import ISiteSupportedLanguageDto from "../../model/SiteSupportedLanguageDto.if";
import IStyleDto from "../../model/StyleDto.if";
import ITourDto from "../../model/TourDto.if";
import ITourMenuItemDto from "../../model/TourMenuItemDto.if";
import ITourRelatedSiteObjectDto from "../../model/TourRelatedSiteObjectDto.if";
import ITourStatusDto from "../../model/TourStatusDto.if";
import ITourSubTypeDto from "../../model/TourSubTypeDto.if";
import ITourSupportedLanguageDto from "../../model/TourSupportedLanguageDto.if";
import ITourTranslationDto from "../../model/TourTranslationDto.if";
import ITourTypeDto from "../../model/TourTypeDto.if";
import { Collection, Table } from "dexie";


function _generateDBMappings(): DexieDBMap {
    let dbMapping = {};

    for ( const entityName in sitePatchMap.mappings ) {
        if ( !sitePatchMap.mappings.hasOwnProperty(entityName) ) { continue; }
        const interfaceName = sitePatchMap.interfaceNameFromEntityName(entityName);
        const modelObj: IBaseDto = (model as any)[entityName]();
        const mappings: Array<IMapProperty> = modelObj.mappings();
        (dbMapping as any)[interfaceName] = '++' +  mappings.map(item => item.localName).join(',');
    }

    return dbMapping;
}

function _instantiator(entityName: string, entityData: object): IBaseDto|null {
    const ctor = (model as any)[entityName];

    let obj: IBaseDto|null = null;

    if ( ctor ) {
        obj = ctor(entityData);
    } 

    return obj;
};

class ContainerTable<T extends IBaseDto> implements IWorkspaceTable<T> {
    private _table: Table<any, any>;
    private _entityName: string;

    constructor (options: IDBTOptions) {
        this._table = options.table;
        this._entityName = options.entityName;
    }

    count(): Promise<number> {
        return this._table.count();
    }

    first<T extends IBaseDto>(): Promise<T> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<T>(function(resolve, reject) {
            table
                .toArray()
                .then( (arr: Array<object>) => {
                    if ( arr.length === 0 ) {
                        reject("No items found")
                    }
                    const item: T|null = _instantiator(entityName, arr[0]) as T;
                    item ? 
                        resolve(item):
                        reject('Failed to instantiate element ' + entityName);
                } )
                .catch( (err: Error) => reject(err) );
        });
    }

    get(pk: any): Promise<T> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<T>(function(resolve, reject) {
            table
                .get(pk)
                .then( (data: object): void => {
                    const item = _instantiator(entityName, data) as T;
                    if ( item != null ) {
                        resolve(item);
                    } else {
                        reject("Unable to instantiate item for pk:" + pk);
                    }
                })
                .catch( (err: Error) => reject(err) );
        });
    }

    all(): Promise<T[]> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<T[]>(function(resolve, reject) {
            table
                .toArray()
                .then( (arr: Array<object>) => {
                    const items: T[] = [];
                    arr.forEach(x => {
                        const item = _instantiator(entityName, x)
                        if ( item ) {
                            items.push(item as T);
                        }
                    })
                    resolve(items);
                } )
                .catch( (err: Error) => reject(err) );
        });
    }

    bulkAddOrUpdate(items: Array<T>): Promise<void> {
        const table = this._table;
        const entityName = this._entityName;
        return new Promise<void>(function(resolve, reject) {
            table
                .bulkPut(items, {allKeys: false})
                .then( () => resolve() )
                .catch( (err: Error) => {
                    reject(err);
                }  )
        })
    }
}


class ContainerDB implements IWorkspaceDB {
    private _db: DB;
    private _openCount: number = 0;

    name: string = "";

    constructor (options: IDBOptions = {}) {
        this._db = new DB({
            version: 1,
            mappings: _generateDBMappings(),
            name: options.name ?? "Workspace",
            indexedDB: options.indexedDB,
            IDBKeyRange: options.IDBKeyRange,
        });

        this.name = this._db.name;
    }

    open(): Promise<ContainerDB> {
        this._openCount += 1;

        if ( this.isOpen() && this._openCount > 1 ) {
            return Promise.resolve(this); /* already open */
        }

        const self = this;

        return new Promise((resolve, reject) => {
            this._db.open()
                .then(_ => resolve(self))
                .catch(err => reject(err));
        });
    }

    close(): void {
        if ( this.isOpen() && this._openCount === 1 ) {
            this._db.close();
        }

        this._openCount = Math.max(0, this._openCount-1);
    }

    isOpen(): boolean {
        return this._db.isOpen();
    }

    getTable<T extends IBaseDto>(interfaceName: string) : ContainerTable<T> {
        const tableName = interfaceName;
        const entityName = sitePatchMap.entityNameFromInterfaceName(interfaceName);
        const table = this._db.table(tableName);
        const container = new ContainerTable<T>({
            entityName,
            table,
        })
        return container;
    }

    first<T extends IBaseDto>(tableName: string): Promise<T> {
        if ( !tableName ) {
            return Promise.reject('No Table to search with');
        }

        const db = this._db;
        return new Promise<T>(function(resolve, reject) {
            db.table(tableName)
                .toArray()
                .then( (items: Array<T>) => {
                    resolve(items[0]);
                })
                .catch( (err: Error) => reject(err) );
        });
    }

    get<T extends IBaseDto>(tableName: string, pk: any): Promise<T> {
        if ( !pk  ) {
            return Promise.reject(`No PK to search with, table:${tableName}, pk:${pk}`);
        }

        const db = this._db;
        return new Promise<T>(function(resolve, reject) {
            db.table(tableName)
                .get(pk)
                .then( (data: object) => {
                    let entityName = sitePatchMap.entityNameFromTableName(tableName);
                    let item = _instantiator(entityName, data) as T;
                    resolve(item);
                })
                .catch( (err: Error) => reject(err) );
        })
    }

    all<T extends IBaseDto>(tableName: string): Promise<Array<T>> {
        if ( !tableName ) {
            return Promise.reject('No Table to search with');
        }

        const db = this._db;
        return new Promise<Array<T>>(function(resolve, reject) {
            db.table(tableName)
                .toArray()
                .then( (items: Array<T>) => {
                    resolve(items);
                })
                .catch( (err: Error) => reject(err) );
        })
    }


    backgroundMode          = () => this.getTable<IBackgroundModeDto>('IBackgroundModeDto');
    beacons                 = () => this.getTable<IBeaconDto>('IBeaconDto');
    beaconSensitivity       = () => this.getTable<IBeaconSensitivityDto>('IBeaconSensitivityDto');
    country                 = () => this.getTable<ICountryDto>('ICountryDto');
    floorPlan               = () => this.getTable<IFloorPlanDto>('IFloorPlanDto');
    fontAlignment           = () => this.getTable<IFontAlignmentDto>('IFontAlignmentDto');
    fontFamily              = () => this.getTable<IFontFamilyDto>('IFontFamilyDto');
    fontSizeUnit            = () => this.getTable<IFontSizeUnitDto>('IFontSizeUnitDto');
    fontStyle               = () => this.getTable<IFontStyleDto>('IFontStyleDto');
    language                = () => this.getTable<ILanguageDto>('ILanguageDto');
    medium                  = () => this.getTable<IMediumDto>('IMediumDto');
    menuItem                = () => this.getTable<IMenuItemDto>('IMenuItemDto');
    menuItemTranslation     = () => this.getTable<IMenuItemTranslationDto>('IMenuItemTranslationDto');
    pinType                 = () => this.getTable<IPinTypeDto>('IPinTypeDto');
    screenOrientation       = () => this.getTable<IScreenOrientationDto>('IScreenOrientationDto');
    site                    = () => this.getTable<ISiteDto>('ISiteDto');
    siteObject              = () => this.getTable<ISiteObjectDto>('ISiteObjectDto');
    siteObjectKeyword       = () => this.getTable<ISiteObjectKeywordDto>('ISiteObjectKeywordDto');
    siteObjectOnFloorPlan   = () => this.getTable<ISiteObjectOnFloorPlanDto>('ISiteObjectOnFloorPlanDto');
    siteObjectTranslation   = () => this.getTable<ISiteObjectTranslationDto>('ISiteObjectTranslationDto');
    siteObjectType          = () => this.getTable<ISiteObjectTypeDto>('ISiteObjectTypeDto');
    siteSupportedLanguage   = () => this.getTable<ISiteSupportedLanguageDto>('ISiteSupportedLanguageDto');
    style                   = () => this.getTable<IStyleDto>('IStyleDto');
    tour                    = () => this.getTable<ITourDto>('ITourDto');
    tourMenuItem            = () => this.getTable<ITourMenuItemDto>('ITourMenuItemDto');
    tourRelatedSiteObject   = () => this.getTable<ITourRelatedSiteObjectDto>('ITourRelatedSiteObjectDto');
    tourStatus              = () => this.getTable<ITourStatusDto>('ITourStatusDto');
    tourSubType             = () => this.getTable<ITourSubTypeDto>('ITourSubTypeDto');
    tourSupportedLanguage   = () => this.getTable<ITourSupportedLanguageDto>('ITourSupportedLanguageDto');
    tourTranslation         = () => this.getTable<ITourTranslationDto>('ITourTranslationDto');
    tourType                = () => this.getTable<ITourTypeDto>('ITourTypeDto');
}

export default ContainerDB;